import React, { useEffect } from 'react'
import { useTrades } from '@/hooks/useTrades'
import LoadingSpinner from './LoadingSpinner'
import ExportCSV, { type ColumnDef } from './ExportCSV'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import type { Trade } from '@/hooks/useTrades'

function fmt(n: number) { return `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}` }

const TRADE_COLUMNS: ColumnDef<Trade>[] = [
  { header: 'Date',   accessor: row => format(new Date(row.date), 'yyyy-MM-dd') },
  { header: 'Asset',  accessor: 'asset' },
  { header: 'Symbol', accessor: 'symbol' },
  { header: 'Side',   accessor: 'side' },
  { header: 'Price',  accessor: 'price' },
  { header: 'Qty',    accessor: 'quantity' },
  { header: 'Value',  accessor: 'value' },
  { header: 'Status', accessor: 'status' },
  { header: 'Agent',  accessor: 'agent' },
]

export default function TradeHistoryTable({ limit = 10 }: { limit?: number }) {
  const { trades, fetchTrades, loadingTrades } = useTrades()

  useEffect(() => { fetchTrades(limit) }, [limit]) // eslint-disable-line

  if (loadingTrades && trades.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 gap-2 text-text-muted text-sm">
        <LoadingSpinner size="sm" /> Loading trades…
      </div>
    )
  }

  const visible = trades.slice(0, limit)

  return (
    <div>
      {/* Toolbar */}
      <div className="flex justify-end px-4 py-2 border-b border-border">
        <ExportCSV
          data={visible}
          columns={TRADE_COLUMNS}
          filename="trade-history"
        />
      </div>

      <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label="Trade history">
        <thead>
          <tr className="border-b border-border text-xs text-text-muted uppercase tracking-wider">
            <th className="py-2.5 px-4 text-left font-medium">Date</th>
            <th className="py-2.5 px-4 text-left font-medium">Asset</th>
            <th className="py-2.5 px-4 text-left font-medium hidden sm:table-cell">Side</th>
            <th className="py-2.5 px-4 text-right font-medium">Value</th>
            <th className="py-2.5 px-4 text-right font-medium hidden md:table-cell">Status</th>
            <th className="py-2.5 px-4 text-right font-medium hidden lg:table-cell">Agent</th>
          </tr>
        </thead>
        <tbody>
          {visible.map(t => (
            <tr key={t.id} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
              <td className="py-3 px-4 text-text-muted text-xs font-mono">
                {format(new Date(t.date), 'dd MMM yy')}
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <span
                    className="trade-dot w-2 h-2 rounded-full shrink-0"
                    style={{ ['--dot-color' as string]: t.color } as React.CSSProperties}
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-text-primary truncate max-w-[140px]">{t.asset}</p>
                    <p className="text-xs text-text-muted font-mono">{t.symbol}</p>
                  </div>
                </div>
              </td>
              <td className="py-3 px-4 hidden sm:table-cell">
                <span className={clsx('badge text-xs', t.side === 'Buy' ? 'badge-green' : 'badge-red')}>
                  {t.side}
                </span>
              </td>
              <td className="py-3 px-4 text-right font-mono text-text-primary">{fmt(t.value)}</td>
              <td className="py-3 px-4 text-right hidden md:table-cell">
                <span className={clsx('badge text-xs',
                  t.status === 'Executed'  ? 'badge-green' :
                  t.status === 'Pending'   ? 'badge-amber' : 'badge-red'
                )}>
                  {t.status}
                </span>
              </td>
              <td className="py-3 px-4 text-right text-xs text-text-muted hidden lg:table-cell">
                {t.agent}
              </td>
            </tr>
          ))}
          {visible.length === 0 && !loadingTrades && (
            <tr>
              <td colSpan={6} className="py-10 text-center text-text-muted text-sm">
                No trade history available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  )
}
